import React from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'

import Box from 'components/Box'
import Icon from 'components/Icon'
import Text from 'components/Text'
import { ErrorMessage, LabelText, RequiredText } from 'components/TextInput'
import { getBorderColor, inputBgColor } from 'components/TextInput/SimpleTextInput'
import Spinner from 'components/Spinner'

import { ThemeColor } from 'utils/colors'

import DatetimePicker from '.'
import { CustomInputWrapperProps, DatetimeInputProps } from './types'
import { formatDatetime } from 'utils/datetime'
import Flex from 'components/Flex'

const getArrow = (props: DatetimeInputProps) => (
  <Icon color={props.disabled || props.readOnly ? 'gray5' : 'blue1'} fontSize={13} icon="chevron-down" />
)

const DatetimeInput: React.FC<DatetimeInputProps> = ({ value, dateFormat = 'PPp', showIcon, ...props }) => {
  const { t } = useTranslation()
  const textProps = {
    color: (props.disabled || props.readOnly ? 'gray4' : 'defaultText') as ThemeColor,
  }
  return (
    <DatetimePicker
      {...props}
      dateFormat={dateFormat}
      canPickTime
      customInput={
        <Box>
          {props.label && (
            <LabelText>
              {t(props.label)}
              {props.required && <RequiredText>*</RequiredText>}
            </LabelText>
          )}
          <InputWrapper {...(props as CustomInputWrapperProps)}>
            <Flex justifyContent="space-between" width="100%">
              <Text {...textProps}>{formatDatetime(value, dateFormat)}</Text>
              {getArrow(props)}
            </Flex>
            {showIcon && <Icon color="gray5" icon={['far', 'calendar-alt']} />}
            {props.isLoading && (
              <LoadingOverlay>
                <Spinner />
              </LoadingOverlay>
            )}
          </InputWrapper>
          {props.error && <ErrorMessage>{props.error}</ErrorMessage>}
        </Box>
      }
      initialValue={value}
      showTimeSelect
    />
  )
}

const InputWrapper = styled.div<CustomInputWrapperProps>`
  align-items: center;
  box-sizing: border-box;
  background-color: ${(p) =>
    p.disabled || p.readOnly ? p.theme.colors.gray1 : p.fill === 'white' ? 'white' : inputBgColor};
  border: 1px solid ${(p) => getBorderColor(p)};
  border-radius: 4px;
  display: flex;
  justify-content: space-between;
  padding: 16px;

  &:hover {
    cursor: ${(p) => (p.disabled || p.readOnly ? 'default' : 'pointer')};
    border-color: ${(p) => (p.disabled || p.readOnly ? p.theme.colors.gray2 : p.theme.colors.blue1)};
  }
`

const LoadingOverlay = styled.div`
  user-select: none;
  cursor: progress;
  box-sizing: border-box;
  position: absolute;
  top: 0;
  right: 0;
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 3rem;
`

export default DatetimeInput
